import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../utils/formatting';
import saleService from '../services/saleService';
import platformService from '../services/platformService';
import { getCountryName } from '../utils/countries';

const ProductStatsScreen = () => {
  // Estados para gestionar los datos y la interfaz
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState({
    products: true,
    countries: true,
    cities: true
  });
  const [error, setError] = useState({
    products: null,
    countries: null,
    cities: null
  });
  
  // Estados para paginación y ordenación
  const [pagination, setPagination] = useState({
    products: {
      currentPage: 1,
      totalPages: 1,
      total: 0
    },
    countries: {
      currentPage: 1,
      totalPages: 1,
      total: 0
    },
    cities: {
      currentPage: 1,
      totalPages: 1,
      total: 0
    }
  });
  const [limit, setLimit] = useState(25);
  const [sortField, setSortField] = useState('totalSales');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Estados para filtros de fecha
  const [filters, setFilters] = useState({
    timeRange: '',
    startDate: '',
    endDate: '',
    platform: '',
    productName: '',
    country: '',
    city: '',
    hasConversion: ''
  });
  
  // Filtros temporales (los que se muestran en la interfaz antes de aplicarlos)
  const [tempFilters, setTempFilters] = useState({
    timeRange: '',
    startDate: '',
    endDate: '',
    platform: '',
    productName: '',
    country: '',
    city: '',
    hasConversion: ''
  });
  
  // Opciones para el selector de elementos por página
  const pageSizeOptions = [10, 25, 50, 100];
  
  // Estado para almacenar las plataformas disponibles
  const [platforms, setPlatforms] = useState([]);
  
  // Estado para los filtros de ubicación
  const [locationFilters, setLocationFilters] = useState({});
  const [availableCities, setAvailableCities] = useState([]);
  
  // Función auxiliar para construir URLs con filtros de fecha
  const buildUrlWithDateFilters = (baseParam, baseValue) => {
    let url = `/sales?${baseParam}=${encodeURIComponent(baseValue)}`;
    
    // Añadir filtros de fecha solo si no son los filtros por defecto (mes actual)
    if (filters.timeRange !== 'thisMonth') {
      if (filters.startDate) {
        url += `&startDate=${filters.startDate}`;
      }
      
      if (filters.endDate) {
        url += `&endDate=${filters.endDate}`;
      }
    }
    
    return url;
  };
  
  // Función para construir URLs hacia ProductStatsFilteredScreen
  const buildFilteredStatsUrl = (country, city = '') => {
    const params = new URLSearchParams();
    
    if (country) {
      params.append('country', country);
    }
    
    if (city) {
      params.append('city', city);
    }
    
    // Añadir el rango de tiempo actual
    if (filters.timeRange && filters.timeRange !== 'thisMonth') {
      params.append('timeRange', filters.timeRange);
    }
    
    // Añadir fechas personalizadas si existen
    if (filters.timeRange === 'custom') {
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }
    }
    
    // Añadir plataforma si está filtrada
    if (filters.platform) {
      params.append('platform', filters.platform);
    }
    
    // Añadir filtro de conversión si está activo
    if (filters.hasConversion) {
      params.append('hasConversion', filters.hasConversion);
    }
    
    return `/product-stats-filtered?${params.toString()}`;
  };
  
  // Función para calcular el rango de fechas según el filtro seleccionado
  const calculateDateRange = (timeRange, customStartDate, customEndDate) => {
    // Crear fechas en la zona horaria de Madrid
    const now = new Date();
    let endDate = new Date(now);
    let startDate = new Date(now);

    if (timeRange === 'custom' && customStartDate && customEndDate) {
      return {
        startDate: customStartDate,
        endDate: customEndDate
      };
    }

    switch (timeRange) {
      case 'lastWeek':
        startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 7);
        endDate = new Date(endDate);
        break;
      case 'lastMonth':
        startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - 1);
        startDate.setDate(1);
        endDate = new Date(endDate);
        endDate.setDate(0); // Último día del mes anterior
        break;
      case 'lastYear':
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 1);
        startDate.setMonth(0);
        startDate.setDate(1);
        endDate = new Date(endDate);
        endDate.setFullYear(endDate.getFullYear() - 1);
        endDate.setMonth(11);
        endDate.setDate(31);
        break;
      case 'last7days':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'last30days':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case 'last90days':
        startDate.setDate(endDate.getDate() - 90);
        break;
      case 'thisWeek':
        startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - endDate.getDay() + 1); // Lunes de esta semana
        endDate = new Date(endDate);
        endDate.setDate(endDate.getDate() - endDate.getDay() + 7); // Domingo de esta semana
        break;
      case 'thisMonth':
        startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
        break;
      case 'thisYear':
      default:
        startDate = new Date(endDate.getFullYear(), 0, 1);
        endDate = new Date(endDate.getFullYear(), 11, 31);
        break;
    }

    // Ajustar las horas para el inicio y fin del día
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    // Formatear las fechas en YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };
  
  // Función para cargar plataformas disponibles
  const loadPlatforms = async () => {
    try {
      const platforms = await platformService.getActivePlatforms();
      // Asegurarnos de que platforms sea un array
      setPlatforms(Array.isArray(platforms) ? platforms : []);
    } catch (err) {
      console.error('Error al cargar plataformas:', err);
      setPlatforms([]); // En caso de error, establecer un array vacío
    }
  };
  
  // Función para cargar los datos de productos
  const loadProductStats = async () => {
    try {
      setLoading(prev => ({ ...prev, products: true }));
      
      console.log("Enviando filtros a la API:", {
        platform: filters.platform,
        productName: filters.productName,
        country: filters.country,
        city: filters.city
      });
      
      const response = await saleService.getProductStats({
        page: pagination.products.currentPage,
        limit,
        sortField,
        sortOrder,
        startDate: filters.startDate,
        endDate: filters.endDate,
        platform: filters.platform,
        productName: filters.productName,
        country: filters.country,
        city: filters.city,
        hasConversion: filters.hasConversion
      });
      
      setProducts(response.products);
      setPagination(prev => ({
        ...prev,
        products: {
          currentPage: response.page,
          totalPages: response.pages,
          total: response.total
        }
      }));
      setError(prev => ({ ...prev, products: null }));
    } catch (err) {
      console.error('Error al cargar estadísticas de productos:', err);
      setError(prev => ({ 
        ...prev, 
        products: 'Error al cargar las estadísticas de productos. Por favor, intente de nuevo.'
      }));
    } finally {
      setLoading(prev => ({ ...prev, products: false }));
    }
  };
  
  // Función para cargar los datos de países
  const loadCountryStats = async () => {
    try {
      setLoading(prev => ({ ...prev, countries: true }));
      
      const response = await saleService.getCountryStats({
        page: pagination.countries.currentPage,
        limit,
        startDate: filters.startDate,
        endDate: filters.endDate,
        platform: filters.platform,
        productName: filters.productName,
        country: filters.country,
        city: filters.city,
        hasConversion: filters.hasConversion
      });
      
      setCountries(response.countries);
      setPagination(prev => ({
        ...prev,
        countries: {
          currentPage: response.page,
          totalPages: response.pages,
          total: response.total
        }
      }));
      setError(prev => ({ ...prev, countries: null }));
    } catch (err) {
      console.error('Error al cargar estadísticas por país:', err);
      setError(prev => ({ 
        ...prev, 
        countries: 'Error al cargar las estadísticas por país. Por favor, intente de nuevo.'
      }));
    } finally {
      setLoading(prev => ({ ...prev, countries: false }));
    }
  };
  
  // Función para cargar los datos de ciudades
  const loadCityStats = async () => {
    try {
      setLoading(prev => ({ ...prev, cities: true }));
      
      const response = await saleService.getCityStats({
        page: pagination.cities.currentPage,
        limit,
        startDate: filters.startDate,
        endDate: filters.endDate,
        platform: filters.platform,
        productName: filters.productName,
        country: filters.country,
        city: filters.city,
        hasConversion: filters.hasConversion
      });
      
      setCities(response.cities);
      setPagination(prev => ({
        ...prev,
        cities: {
          currentPage: response.page,
          totalPages: response.pages,
          total: response.total
        }
      }));
      setError(prev => ({ ...prev, cities: null }));
    } catch (err) {
      console.error('Error al cargar estadísticas por ciudad:', err);
      setError(prev => ({ 
        ...prev, 
        cities: 'Error al cargar las estadísticas por ciudad. Por favor, intente de nuevo.'
      }));
    } finally {
      setLoading(prev => ({ ...prev, cities: false }));
    }
  };
  
  // Cargar filtros de ubicación al montar el componente
  useEffect(() => {
    const fetchLocationFilters = async () => {
      try {
        const filters = await saleService.getLocationFilters();
        setLocationFilters(filters);
      } catch (error) {
        console.error('Error al cargar filtros de ubicación:', error);
      }
    };
    fetchLocationFilters();
  }, []);
  
  // Cargar todos los datos al montar el componente
  useEffect(() => {
    // Cargar plataformas disponibles
    loadPlatforms();
  }, []);
  
  // Recargar datos cuando cambian los filtros
  useEffect(() => {
    loadProductStats();
    loadCountryStats();
    loadCityStats();
  }, [filters]);
  
  // Recargar productos cuando cambian los parámetros de paginación u ordenación
  useEffect(() => {
    loadProductStats();
  }, [pagination.products.currentPage, limit, sortField, sortOrder]);
  
  // Recargar países cuando cambia su paginación
  useEffect(() => {
    loadCountryStats();
  }, [pagination.countries.currentPage]);
  
  // Recargar ciudades cuando cambia su paginación
  useEffect(() => {
    loadCityStats();
  }, [pagination.cities.currentPage]);
  
  // Manejador para cambiar el campo de ordenación
  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortOrder('DESC');
    }
    setPagination(prev => ({
      ...prev,
      products: {
        ...prev.products,
        currentPage: 1
      }
    }));
  };
  
  // Manejador para cambiar el número de elementos por página
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPagination(prev => ({
      ...prev,
      products: {
        ...prev.products,
        currentPage: 1
      }
    }));
  };

  // Manejar cambio en selección de rango de tiempo
  const handleTimeRangeChange = (e) => {
    const newTimeRange = e.target.value;
    setTempFilters(prev => ({
      ...prev,
      timeRange: newTimeRange
    }));
    
    // Si no es rango personalizado, actualizar fechas automáticamente
    if (newTimeRange !== 'custom') {
      const dateRange = calculateDateRange(newTimeRange);
      setTempFilters(prev => ({
        ...prev,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      }));
    }
  };
  
  // Aplicar filtros
  const applyFilters = () => {
    setFilters(tempFilters);
    setPagination(prev => ({
      ...prev,
      products: {
        ...prev.products,
        currentPage: 1
      },
      countries: {
        ...prev.countries,
        currentPage: 1
      },
      cities: {
        ...prev.cities,
        currentPage: 1
      }
    }));
  };
  
  // Limpiar filtros
  const clearFilters = () => {
    const dateRange = calculateDateRange('thisMonth');
    const defaultFilters = {
      timeRange: 'thisMonth',
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      platform: '',
      productName: '',
      country: '',
      city: '',
      hasConversion: ''
    };
    
    setTempFilters(defaultFilters);
    setFilters(defaultFilters);
    setPagination(prev => ({
      ...prev,
      products: {
        ...prev.products,
        currentPage: 1
      },
      countries: {
        ...prev.countries,
        currentPage: 1
      },
      cities: {
        ...prev.cities,
        currentPage: 1
      }
    }));
  };
  
  // Renderizar el indicador de ordenación
  const renderSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === 'ASC' ? '↑' : '↓';
    }
    return '⇅';
  };

  // Manejador para cambiar página
  const handlePageChange = (type, newPage) => {
    setPagination(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        currentPage: newPage
      }
    }));
  };

  // Componente de paginación reutilizable
  const PaginationControls = ({ type }) => {
    const { currentPage, totalPages } = pagination[type];

    // Asegurarse de que totalPages es un número válido y positivo
    const safeCurrentPage = Number(currentPage) || 1;
    const safeTotalPages = Number(totalPages) || 0;

    if (safeTotalPages <= 1) return null;

    // Función para generar botones de página
    const renderPageButtons = () => {
      // Límite de páginas a mostrar
      const maxPagesToShow = 5;
      
      // Protección contra valores inválidos
      if (safeTotalPages <= 0) return null;
      
      const pageButtons = [];
      
      // Calcular el rango de páginas a mostrar
      let startPage = Math.max(1, Math.min(safeCurrentPage - Math.floor(maxPagesToShow / 2), safeTotalPages - maxPagesToShow + 1));
      if (startPage < 1) startPage = 1;
      
      let endPage = Math.min(startPage + maxPagesToShow - 1, safeTotalPages);
      if (endPage > safeTotalPages) endPage = safeTotalPages;
      
      // Generar botones para cada página en el rango
      for (let i = startPage; i <= endPage; i++) {
        pageButtons.push(
          <button
            key={i}
            className={`join-item btn btn-sm ${safeCurrentPage === i ? 'btn-active' : ''}`}
            onClick={() => handlePageChange(type, i)}
          >
            {i}
          </button>
        );
      }
      
      return pageButtons;
    };

    return (
      <div className="flex justify-center mt-4">
        <div className="join">
          <button 
            className="join-item btn btn-sm" 
            onClick={() => handlePageChange(type, 1)}
            disabled={safeCurrentPage === 1}
          >
            «
          </button>
          <button 
            className="join-item btn btn-sm" 
            onClick={() => handlePageChange(type, Math.max(1, safeCurrentPage - 1))}
            disabled={safeCurrentPage === 1}
          >
            ‹
          </button>
          
          {renderPageButtons()}
          
          <button 
            className="join-item btn btn-sm" 
            onClick={() => handlePageChange(type, Math.min(safeTotalPages, safeCurrentPage + 1))}
            disabled={safeCurrentPage === safeTotalPages}
          >
            ›
          </button>
          <button 
            className="join-item btn btn-sm" 
            onClick={() => handlePageChange(type, safeTotalPages)}
            disabled={safeCurrentPage === safeTotalPages}
          >
            »
          </button>
        </div>
      </div>
    );
  };

  // Verificar si alguna tabla está cargando
  const isLoading = loading.products || loading.countries || loading.cities;

  // Mostrar spinner solo durante la carga inicial
  if (isLoading && !products?.length && !countries?.length && !cities?.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  // Manejar cambio de país
  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    
    // Actualizar las ciudades disponibles para el país seleccionado
    if (selectedCountry && selectedCountry !== 'none' && locationFilters[selectedCountry]) {
      setAvailableCities(locationFilters[selectedCountry]);
    } else {
      setAvailableCities([]);
    }
    
    // Actualizar los filtros temporales
    setTempFilters(prev => ({
      ...prev,
      country: selectedCountry,
      city: '' // Resetear ciudad cuando cambia el país
    }));
  };

  // Manejar cambio de ciudad
  const handleCityChange = (e) => {
    const selectedCity = e.target.value;
    setTempFilters(prev => ({
      ...prev,
      city: selectedCity
    }));
  };

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Estadísticas por Productos</h1>
        <div className="page-actions">
          <Link to="/dashboard" className="btn btn-primary">
            Volver al Dashboard
          </Link>
          <div className="form-control">
            <select
              className="select select-bordered"
              value={limit}
              onChange={handleLimitChange}
            >
              {pageSizeOptions.map(option => (
                <option key={option} value={option}>{option} por página</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Filtros de fecha */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title">Filtros</h2>
          
          {/* Filtros temporales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Rango de Tiempo</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.timeRange}
                onChange={handleTimeRangeChange}
                name="timeRange"
              >
                <option value="">Seleccionar rango de tiempo</option>
                <option value="lastWeek">Semana Anterior</option>
                <option value="lastMonth">Mes Anterior</option>
                <option value="lastYear">Año Anterior</option>
                <option value="last7days">Últimos 7 días</option>
                <option value="last30days">Últimos 30 días</option>
                <option value="last90days">Últimos 90 días</option>
                <option value="thisWeek">Semana Actual</option>
                <option value="thisMonth">Mes Actual</option>
                <option value="thisYear">Año Actual</option>
                <option value="custom">Rango Personalizado</option>
              </select>
            </div>

            {tempFilters.timeRange === 'custom' && (
              <>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Fecha Inicio</span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered w-full"
                    value={tempFilters.startDate}
                    onChange={(e) => setTempFilters(prev => ({
                      ...prev,
                      startDate: e.target.value
                    }))}
                    name="startDate"
                  />
                </div>

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Fecha Fin</span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered w-full"
                    value={tempFilters.endDate}
                    onChange={(e) => setTempFilters(prev => ({
                      ...prev,
                      endDate: e.target.value
                    }))}
                    name="endDate"
                  />
                </div>
              </>
            )}
          </div>
          
          <h3 className="font-semibold text-lg mt-4">Filtros adicionales</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Plataforma</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.platform}
                onChange={(e) => setTempFilters(prev => ({
                  ...prev,
                  platform: e.target.value
                }))}
                name="platform"
              >
                <option value="">Todas las plataformas</option>
                {Array.isArray(platforms) && platforms.map(platform => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Tipo de Conversión</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.hasConversion}
                onChange={(e) => setTempFilters(prev => ({
                  ...prev,
                  hasConversion: e.target.value
                }))}
                name="hasConversion"
              >
                <option value="">Todas las ventas</option>
                <option value="true">Ventas con conversión</option>
                <option value="false">Ventas sin conversión</option>
              </select>
            </div>
            
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Nombre de Producto</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={tempFilters.productName}
                onChange={(e) => setTempFilters(prev => ({
                  ...prev,
                  productName: e.target.value
                }))}
                placeholder="Buscar por nombre..."
                name="productName"
              />
            </div>
            
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">País</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.country}
                onChange={handleCountryChange}
                name="country"
              >
                <option value="">Todos los países</option>
                <option value="none">No definido</option>
                {Object.keys(locationFilters).map((country) => (
                  <option key={country} value={country}>
                    {getCountryName(country)}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Ciudad</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={tempFilters.city}
                onChange={handleCityChange}
                name="city"
                disabled={!tempFilters.country || tempFilters.country === 'none'}
              >
                <option value="">Todas las ciudades</option>
                {availableCities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
              {tempFilters.country && tempFilters.country !== 'none' && availableCities.length === 0 && (
                <div className="text-sm text-error mt-1">
                  No hay ciudades disponibles para este país
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end mt-4 gap-2">
            {loading.products && (
              <div className="flex items-center">
                <span className="loading loading-spinner loading-sm mr-2"></span>
                <span>Cargando...</span>
              </div>
            )}
            <button 
              className="btn btn-ghost"
              onClick={clearFilters}
            >
              Limpiar Filtros
            </button>
            <button 
              className="btn btn-primary"
              onClick={applyFilters}
              disabled={loading.products}
            >
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de productos más vendidos */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title">Productos más Vendidos</h2>
          <div className="text-sm text-gray-500">Total: {pagination.products.total} productos</div>
          
          {error.products && (
            <div className="alert alert-error">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error.products}</span>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Plataforma</th>
                  <th>País</th>
                  <th>Ciudad</th>
                  <th 
                    className="cursor-pointer"
                    onClick={() => handleSort('totalSales')}
                  >
                    Ventas Totales {renderSortIcon('totalSales')}
                  </th>
                  <th>% Canceladas</th>
                  <th 
                    className="cursor-pointer"
                    onClick={() => handleSort('totalCommissionEUR')}
                  >
                    Comisión Total {renderSortIcon('totalCommissionEUR')}
                  </th>
                  <th 
                    className="cursor-pointer"
                    onClick={() => handleSort('avgCommissionEUR')}
                  >
                    Comisión Media (Pend/Apr) {renderSortIcon('avgCommissionEUR')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading.products ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      <span className="loading loading-spinner loading-sm"></span>
                    </td>
                  </tr>
                ) : products?.length > 0 ? (
                  products.map((product, index) => (
                    <tr key={index}>
                      <td>
                        {product.productId ? (
                          <Link 
                            to={`/product-stats/${product.productId}`}
                            className="link link-hover link-primary"
                          >
                            {product.productName}
                          </Link>
                        ) : (
                          product.productName
                        )}
                      </td>
                      <td>
                        <span 
                          className="badge badge-outline"
                          style={{ 
                            '--badge-color': product.platformColor || '#808080'
                          }}
                        >
                          {product.platformName}
                        </span>
                      </td>
                      <td>{getCountryName(product.country) || '-'}</td>
                      <td>{product.city || '-'}</td>
                      <td>{product.totalSales}</td>
                      <td>{product.canceledPercentage ? `${product.canceledPercentage}%` : '0%'}</td>
                      <td>{formatCurrency(product.totalCommissionEUR)}</td>
                      <td>{formatCurrency(product.avgCommissionEUR)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No hay productos para mostrar
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <PaginationControls type="products" />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Tabla de países con más ventas */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Ventas por País</h2>
            <div className="text-sm text-gray-500">Total: {pagination.countries.total} países</div>
            
            {error.countries && (
              <div className="alert alert-error">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error.countries}</span>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>País</th>
                    <th>Ventas Totales</th>
                    <th>% Canceladas</th>
                    <th>Comisión Total</th>
                    <th>Comisión Media (Pend/Apr)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading.countries ? (
                    <tr>
                      <td colSpan="5" className="text-center">
                        <span className="loading loading-spinner loading-sm"></span>
                      </td>
                    </tr>
                  ) : countries?.length > 0 ? (
                    countries.map((country, index) => (
                      <tr key={index}>
                        <td>
                          <Link 
                            to={buildFilteredStatsUrl(country.country)}
                            className="link link-hover link-primary"
                          >
                            {getCountryName(country.country)}
                          </Link>
                        </td>
                        <td>{country.totalSales}</td>
                        <td>{country.canceledPercentage ? `${country.canceledPercentage}%` : '0%'}</td>
                        <td>{formatCurrency(country.totalCommissionEUR)}</td>
                        <td>{formatCurrency(country.avgPendingApprovedCommissionEUR)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No hay datos de países para mostrar
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <PaginationControls type="countries" />
          </div>
        </div>
        
        {/* Tabla de ciudades con más ventas */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Ventas por Ciudad</h2>
            <div className="text-sm text-gray-500">Total: {pagination.cities.total} ciudades</div>
            
            {error.cities && (
              <div className="alert alert-error">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error.cities}</span>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Ciudad</th>
                    <th>País</th>
                    <th>Ventas Totales</th>
                    <th>% Canceladas</th>
                    <th>Comisión Total</th>
                    <th>Comisión Media (Pend/Apr)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading.cities ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <span className="loading loading-spinner loading-sm"></span>
                      </td>
                    </tr>
                  ) : cities?.length > 0 ? (
                    cities.map((city, index) => (
                      <tr key={index}>
                        <td>
                          <Link 
                            to={buildFilteredStatsUrl(city.country, city.city)}
                            className="link link-hover link-primary"
                          >
                            {city.city}
                          </Link>
                        </td>
                        <td>
                          <Link 
                            to={buildFilteredStatsUrl(city.country)}
                            className="link link-hover link-primary"
                          >
                            {getCountryName(city.country)}
                          </Link>
                        </td>
                        <td>{city.totalSales}</td>
                        <td>{city.canceledPercentage ? `${city.canceledPercentage}%` : '0%'}</td>
                        <td>{formatCurrency(city.totalCommissionEUR)}</td>
                        <td>{formatCurrency(city.avgPendingApprovedCommissionEUR)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No hay datos de ciudades para mostrar
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <PaginationControls type="cities" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductStatsScreen; 
